import type { ExerciseType } from '@/api/exercises'

/**
 * Формы `content` и `answers` по типам заданий — зеркало серверных правил
 * (`ExerciseContentValidator`) и чекеров.
 *
 * Держим их в одном файле, а не рядом с каждым редактором: редактор и
 * рендерер одного типа обязаны понимать одну и ту же структуру, и разъехаться
 * они могут только вместе с этим файлом.
 */

export type GapsContent = {
  text: string
  /** Ключ пропуска — его порядковый номер в тексте (плейсхолдеры {{1}}, {{2}}, …) */
  gaps: Record<string, string[] | string>
  bank?: string[]
}

export type DropdownGap = { options: string[]; correct: number }

export type DropdownGapsContent = {
  text: string
  gaps: DropdownGap[]
}

export type ChoiceOption = { text: string; correct?: boolean }

export type ChoiceContent = {
  question: string
  multiple?: boolean
  options: ChoiceOption[]
}

export type TrueFalseAnswer = 'true' | 'false' | 'not_stated'

export type TrueFalseContent = {
  text?: string | null
  allow_not_stated?: boolean
  statements: Array<{ text: string; answer?: TrueFalseAnswer }>
}

export type MatchingContent = {
  /** У ученика в паре только левый элемент: `right` — это и был бы ответ */
  pairs: Array<{ left: string; right?: string }>
  /** Правая колонка ученику: перемешанный список без привязки к парам */
  options?: string[]
}

export type SentenceBuilderContent = {
  words: string[]
  accepted: string[]
}

export type OrderingContent = {
  /** Учителю — правильный порядок, ученику — те же тексты, но перемешанные */
  items: string[]
}

export type GroupingContent = {
  groups: string[]
  items: Array<{ text: string; group?: number }>
}

export type ErrorCorrectionContent = {
  tokens: string[]
  error_index?: number
  mode: 'click' | 'click_and_fix'
  corrections?: string[]
}

export type TranslationContent = {
  source: string
  direction: 'ru-en' | 'en-ru'
  accepted?: string[]
}

/**
 * Область на картинке. `x`/`y` — центр в долях от размера картинки, `w`/`h` —
 * размер области там же. Без `w`/`h` область рисуется точкой: так выглядят
 * задания, созданные до выделения областей мышью.
 */
export type PictureHotspot = { x: number; y: number; w?: number; h?: number; answers?: string[] }

export type PictureContent = {
  variant: 'label_hotspots' | 'pick_image'
  image_path?: string | null
  image_url?: string | null
  hotspots?: PictureHotspot[]
  prompt?: string | null
  options?: Array<{ image_path: string; image_url?: string; correct?: boolean }>
}

/**
 * `number` есть только у клетки-начала слова и только у сеток, собранных после
 * появления нумерации: у сохранённых раньше его нет, и рендерер обязан
 * показывать такую сетку без цифр, а не падать.
 */
export type CrosswordCell = { row: number; col: number; letter?: string; number?: number }

export type CrosswordPlacement = {
  direction: 'across' | 'down'
  number?: number
  clue?: string
  answer?: string
  cells: CrosswordCell[]
}

/**
 * Сетка кроссворда/филворда.
 *
 * Размеры приходят в двух формах: плоские `rows`/`cols` — канон клиента, а
 * `size.rows`/`size.cols` — то, как их клал генератор сетки раньше и как они
 * лежат в уже сохранённом контенте. Читать нужно обе: пока клиент знал только
 * плоскую форму, сетка сохранённого задания рисовалась в ноль колонок и ученик
 * не мог ввести ни буквы.
 *
 * `placements` необязательны: у филворда их вычищает санитайзер (по координатам
 * клеток слова читались бы прямо из сетки букв).
 */
export type CrosswordGrid = {
  rows?: number
  cols?: number
  size?: { rows?: number; cols?: number }
  placements?: CrosswordPlacement[]
  letters?: string[][]
}

export type CrosswordContent = {
  variant: 'crossword' | 'wordsearch'
  words: Array<{ answer?: string; clue?: string }>
  grid?: CrosswordGrid
}

/** Размеры сетки: плоские `rows`/`cols` с фолбэком на легаси-форму `size` */
export function crosswordGridSize(grid?: CrosswordGrid | null): { rows: number; cols: number } {
  const rows = Number(grid?.rows ?? grid?.size?.rows ?? 0)
  const cols = Number(grid?.cols ?? grid?.size?.cols ?? 0)

  // NaN и отрицательные значения превращали бы Array.from({length}) в пустой
  // массив, а gridTemplateColumns — в repeat(NaN, …): лучше честный ноль,
  // по которому рендерер покажет «сетка не построена»
  return {
    rows: Number.isFinite(rows) && rows > 0 ? Math.trunc(rows) : 0,
    cols: Number.isFinite(cols) && cols > 0 ? Math.trunc(cols) : 0,
  }
}

export type ListeningContent = {
  audio: { source: 'tts' | 'upload'; text?: string | null; path?: string | null; url?: string | null }
  max_plays?: number | null
  questions: Array<{ type: ExerciseType; content: Record<string, unknown> }>
}

export type OpenAnswerContent = {
  prompt: string
  min_words?: number | null
  image_path?: string | null
  image_url?: string | null
}

export type OralAnswerContent = {
  prompt: string
  max_duration_sec?: number | null
}

/** Статус записи диктанта: синтез платный и идёт отдельным шагом от сохранения */
export type DictationAudioStatus = 'none' | 'pending' | 'ready' | 'failed'

export type DictationContent = {
  source_text: string
  /** Альтернативные написания целиком — ответ сверяется со всей фразой, а не по словам */
  accepted_answers?: string[]
  max_plays?: number | null
  ignore_case?: boolean
  ignore_punctuation?: boolean
  /** Десятичной строкой, как её принимает и отдаёт сервер: '0.90', '1.00' */
  playback_rate?: string | number
  audio?: {
    tts_audio_id?: number | null
    path?: string | null
    url?: string | null
    status?: DictationAudioStatus
  }
}

/** Пустая заготовка контента для нового задания каждого типа */
export function defaultContentFor(type: ExerciseType): Record<string, unknown> {
  switch (type) {
    case 'drag_gaps':
      return { text: '', gaps: {}, bank: [] }
    case 'cloze':
      return { text: '', gaps: {} }
    case 'dropdown_gaps':
      return { text: '', gaps: [] }
    case 'choice':
      return { question: '', multiple: false, options: [{ text: '', correct: true }, { text: '' }] }
    case 'true_false':
      return { text: '', allow_not_stated: false, statements: [{ text: '', answer: 'true' }] }
    case 'matching':
      return { pairs: [{ left: '', right: '' }, { left: '', right: '' }] }
    case 'sentence_builder':
      return { words: [], accepted: [''] }
    case 'ordering':
      return { items: ['', ''] }
    case 'grouping':
      return { groups: ['', ''], items: [{ text: '', group: 0 }, { text: '', group: 1 }] }
    case 'error_correction':
      return { tokens: [], error_index: 0, mode: 'click', corrections: [] }
    case 'translation':
      return { source: '', direction: 'ru-en', accepted: [''] }
    case 'picture':
      return { variant: 'label_hotspots', image_path: null, hotspots: [] }
    case 'crossword':
      return { variant: 'crossword', words: [{ answer: '', clue: '' }, { answer: '', clue: '' }] }
    case 'listening':
      return { audio: { source: 'tts', text: '' }, max_plays: 3, questions: [] }
    case 'open_answer':
      return { prompt: '', min_words: null }
    case 'oral_answer':
      return { prompt: '', max_duration_sec: 120 }
    case 'dictation':
      return {
        source_text: '',
        accepted_answers: [],
        max_plays: 3,
        ignore_case: true,
        ignore_punctuation: true,
        playback_rate: '1.00',
        audio: { tts_audio_id: null, path: null, url: null, status: 'none' },
      }
    default:
      return {}
  }
}

/** Плейсхолдер пропуска в тексте: {{1}}, {{2}}, … */
export const GAP_PATTERN = /\{\{(\d+)\}\}/g

/** Разбор текста с пропусками на куски: строка или номер пропуска */
export function splitByGaps(text: string): Array<{ kind: 'text'; value: string } | { kind: 'gap'; index: number }> {
  const parts: Array<{ kind: 'text'; value: string } | { kind: 'gap'; index: number }> = []
  let cursor = 0

  for (const match of text.matchAll(GAP_PATTERN)) {
    const at = match.index ?? 0
    if (at > cursor) parts.push({ kind: 'text', value: text.slice(cursor, at) })
    parts.push({ kind: 'gap', index: Number(match[1]) })
    cursor = at + match[0].length
  }

  if (cursor < text.length) parts.push({ kind: 'text', value: text.slice(cursor) })

  return parts
}

/** Номера пропусков в тексте по порядку появления */
export function gapNumbers(text: string): number[] {
  return [...text.matchAll(GAP_PATTERN)].map((match) => Number(match[1]))
}
